import axios from 'axios';
import * as UnofficialMessageService from './UnofficialMessageService';
import SessionService from './SessionService';
import { APP_TIMEZONE } from '../utils/dayjsConfig';
import type { GroupMessageDto } from './GroupService';

// Obter a URL da variável de ambiente
const WHATSAPP_API_URL = process.env.REACT_APP_WHATSAPP_API_URL;
// const UNOFFICIAL_API_URL = process.env.REACT_APP_UNOFFICIAL_API_URL || '/unofficial';
// Verificação de segurança para garantir que não estamos usando a porta 5002
const getApiUrl = () => {
  if (!WHATSAPP_API_URL) {
    return 'https://whatsapp.ligchat.com/api/v1';
  }

  if (WHATSAPP_API_URL.includes(':5002')) {
    return WHATSAPP_API_URL.replace(':5002', '');
  }

  return WHATSAPP_API_URL;
};

const API_URL = getApiUrl();

/**
 * Sanitiza nome de arquivo antes do envio.
 *
 * Caracteres como colchetes, parênteses, espaços, acentos e símbolos quebram
 * o `Content-Disposition` multipart em alguns backends e o pipeline do
 * WhatsApp/Meta rejeita ou trunca certos nomes. Mantém apenas ASCII básico
 * (letras, dígitos, `.`, `-`, `_`) substituindo o resto por `_`.
 */
export function sanitizeFileName(name: string | null | undefined): string {
  const raw = (name ?? '').trim();
  if (!raw) return 'arquivo';
  // Descobre a extensão pelo último ponto, depois de remover espaços antes/depois
  // (ex.: "Extra .pdf" → base "Extra", ext "pdf").
  const dotIdx = raw.lastIndexOf('.');
  const base = (dotIdx > 0 ? raw.slice(0, dotIdx) : raw).trim();
  const ext = (dotIdx > 0 ? raw.slice(dotIdx + 1) : '').trim();
  // Remove combining marks (acentos decompostos pelo NFKD). Usa RegExp construído
  // com string para escapar U+0300–U+036F de forma robusta contra encoding do editor.
  const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');
  const stripDiacritics = (s: string): string =>
    s.normalize('NFKD').replace(COMBINING_MARKS, '');
  const norm = (s: string): string =>
    stripDiacritics(s)
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_.-]+|[_.-]+$/g, '');
  const safeBase = norm(base) || 'arquivo';
  const safeExt = ext ? norm(ext) : '';
  return safeExt ? `${safeBase}.${safeExt}` : safeBase;
}

// Interface para a resposta da API
export interface MessageResponse {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  whatsappMessageID?: string; // ID da mensagem no WhatsApp
  replyToMessageID?: string; // ID da mensagem respondida no WhatsApp
  repliedMessage?: MessageResponse; // Mensagem completa sendo respondida
  senderName?: string; // Nome do remetente
  senderUserId?: number; // ID do usuário remetente
  isAnonymous?: boolean; // Se a mensagem foi enviada em modo anônimo
  messageStatus?: 'sent' | 'delivered' | 'received' | 'read'; // Status da mensagem
  /** Emoji da reação do contacto à mensagem enviada por nós (1:1) */
  reactionEmoji?: string | null;
  /** Emoji da reação do operador (Ligchat) à mensagem recebida ou enviada (1:1) */
  operatorReactionEmoji?: string | null;
  /** Mensagem encaminhada via WhatsApp */
  isForwarded?: boolean;
}

// Interface para a resposta completa da API de envio
export interface SendMessageApiResponse {
  status: string;
  message: string;
  data: MessageResponse;
  timestamp: string;
}

// Interfaces para as requisições
export interface SendMessageDto {
  to: string;
  recipientPhone: string;
  text: string;
  contactId: number;
  sectorId: number;
  userId: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export interface SendInteractiveReplyDto {
  recipientPhone: string;
  /** Preferencial: JID completo da conversa (chatJId do payload interativo). */
  chatJId?: string;
  sectorId: number;
  userId: number;
  isAnonymous: boolean;
  stanzaId: string;
  kind: 'buttons_response' | 'list_response' | 'native_flow_response';
  selectedButtonId?: string;
  selectedDisplayText?: string;
  listTitle?: string;
  listDescription?: string;
  listRowId?: string;
  nativeFlowName?: string;
  nativeFlowParamsJson?: string;
  nativeFlowVersion?: number;
}

// Constantes para tipos de áudio suportados
const SUPPORTED_AUDIO_TYPES = ['audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/wav'];

export interface SendFileDto {
  file?: File;
  base64File?: string;
  fileName: string;
  mediaType: string;
  caption?: string;
  recipient: string;
  recipientPhone: string;
  contactId: number;
  sectorId: number;
  isHuman?: boolean;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export interface SendGroupMessageDto {
  sectorId: number;
  groupId: number;
  message: string;
  userId?: number;
  isAnonymous?: boolean;
  replyToMessageId?: string;
}

export interface SendGroupMediaDto {
  sectorId: number;
  groupId: number;
  base64File: string;
  fileName: string;
  mediaType?: string;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  replyToMessageId?: string;
}

export interface MessageType {
  id: number;
  content: string | null;
  isSent: boolean;
  mediaType?: string;
  mediaUrl?: string | null;
  fileName?: string;
  sectorId?: number;
  contactID: number;
  isRead: boolean;
  sentAt?: string;
  attachment?: {
    url: string;
    type: string;
    name: string;
  };
}

// Interface para a nova resposta da API de mensagens
interface GetMessagesApiResponse {
  status: string;
  message: string;
  data: {
    messages: MessageResponse[];
    total: number;
    hasMore: boolean;
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
      nextOffset: number;
    };
    contact: {
      id: number;
      name: string;
      number: string;
      avatarUrl: string;
    };
  };
  timestamp: string;
}

// Interface para o retorno da função com informações de paginação
export interface GetMessagesResult {
  messages: MessageResponse[];
  hasMore: boolean;
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
    nextOffset: number;
  };
}

// Função para buscar mensagens de um contato
export const getMessagesByContactId = async (
  contactId: number,
  limit: number = 10,
  offset: number = 0
): Promise<MessageResponse[]> => {
  try {
    const response = await axios.get<GetMessagesApiResponse>(
      `${API_URL}/contact/${contactId}/messages`,
      {
        params: { limit, offset },
        headers: {
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );


    // Verificar se a resposta tem a estrutura esperada
    if (response.data && response.data.data && response.data.data.messages) {
      // Retornar apenas o array de mensagens para manter compatibilidade
      return response.data.data.messages;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

// Nova função que retorna mensagens com informações de paginação
export const getMessagesByContactIdWithPagination = async (
  contactId: number,
  limit: number = 10,
  offset: number = 0
): Promise<GetMessagesResult> => {
  try {
    const response = await axios.get<GetMessagesApiResponse>(
      `${API_URL}/contact/${contactId}/messages`,
      {
        params: { limit, offset },
        headers: {
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );


    // Verificar se a resposta tem a estrutura esperada
    if (response.data && response.data.data && response.data.data.messages) {
      return {
        messages: response.data.data.messages,
        hasMore: response.data.data.hasMore,
        total: response.data.data.total,
        pagination: response.data.data.pagination
      };
    } else {
      return {
        messages: [],
        hasMore: false,
        total: 0,
        pagination: {
          limit,
          offset,
          total: 0,
          hasMore: false,
          nextOffset: offset + limit
        }
      };
    }
  } catch (error) {
    return {
      messages: [],
      hasMore: false,
      total: 0,
      pagination: {
        limit,
        offset,
        total: 0,
        hasMore: false,
        nextOffset: offset + limit
      }
    };
  }
};

// Função para enviar mensagem de texto
export const sendMessage = async (message: SendMessageDto): Promise<MessageResponse> => {
  try {
    if (!message.to || !message.text || !message.recipientPhone) {
      throw new Error('Campos obrigatórios não preenchidos');
    }

    const isOfficial = SessionService.getSectorIsOfficial();

    if (!isOfficial) {
      // Usar o serviço não oficial
      const unofficialResponse = await UnofficialMessageService.sendTextMessage({
        sector_id: message.sectorId,
        recipient: message.recipientPhone,
        message: message.text,
        userId: message.userId,
        isAnonymous: message.isAnonymous,
        sentAt: message.sentAt || new Date().toISOString(),
        replyToMessageId: message.replyToMessageId,
        ...(message.timezone ? { timezone: message.timezone } : {})
      });

      // Verificar se a resposta é válida
      if (!unofficialResponse || unofficialResponse.status === 'error') {
        throw new Error(unofficialResponse?.message || 'Erro ao enviar mensagem');
      }

      // API Go devolve MessageResponse completo em data (id do banco, content com *Nome:* se aplicável).
      const d = unofficialResponse.data;
      const resolvedId = typeof d?.id === 'number' ? d.id : Date.now();
      return {
        id: resolvedId,
        content: d?.content ?? message.text,
        mediaType: d?.mediaType ?? 'text',
        mediaUrl: d?.mediaUrl ?? null,
        fileName: d?.fileName ?? null,
        mimeType: d?.mimeType ?? null,
        sectorId: d?.sectorId ?? message.sectorId,
        contactID: d?.contactID ?? message.contactId,
        sentAt:
          (typeof d?.sentAt === 'string' && d.sentAt) ||
          unofficialResponse.timestamp ||
          message.sentAt ||
          new Date().toISOString(),
        isSent: unofficialResponse.status === 'success',
        isRead: d?.isRead ?? false,
        whatsappMessageID: d?.whatsappMessageID || undefined,
        replyToMessageID: d?.replyToMessageID || message.replyToMessageId || undefined,
        repliedMessage: d?.repliedMessage || undefined,
        messageStatus: (d?.messageStatus as MessageResponse['messageStatus']) || ('sent' as const)
      };
    }

    // Usar o serviço oficial
    const payload = {
      to: message.to,
      recipientPhone: message.recipientPhone,
      text: message.text,
      contactId: message.contactId,
      sectorId: message.sectorId,
      userId: message.userId,
      isAnonymous: message.isAnonymous,
      sentAt: message.sentAt || new Date().toISOString(),
      timezone: message.timezone || APP_TIMEZONE,
      replyToMessageId: message.replyToMessageId
    };

    console.log('Payload sendo enviado para send-message:', payload);

    const response = await axios.post<SendMessageApiResponse>(`${API_URL}/whatsapp/send-message`, payload);
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

export const sendInteractiveReply = async (dto: SendInteractiveReplyDto): Promise<void> => {
  const basePayload = {
    sector_id: dto.sectorId,
    recipient: dto.recipientPhone,
    chat_jid: dto.chatJId ?? '',
    userId: dto.userId,
    isAnonymous: dto.isAnonymous,
    stanza_id: dto.stanzaId,
    kind: dto.kind,
    selected_button_id: dto.selectedButtonId ?? '',
    selected_display_text: dto.selectedDisplayText ?? '',
    list_title: dto.listTitle ?? '',
    list_description: dto.listDescription ?? '',
    list_row_id: dto.listRowId ?? '',
    native_flow_name: dto.nativeFlowName ?? '',
    native_flow_params_json: dto.nativeFlowParamsJson ?? '',
    native_flow_version: dto.nativeFlowVersion ?? 0
  };

  const isOfficial = SessionService.getSectorIsOfficial();

  if (!isOfficial) {
    await UnofficialMessageService.sendInteractiveReply(basePayload);
    return;
  }

  await axios.post(`${API_URL}/whatsapp/send-interactive-reply`, basePayload);
};

export const sendGroupMessage = async (
  dto: SendGroupMessageDto
): Promise<{ sentAt: string; message?: GroupMessageDto }> => {
  const baseUrl = getApiUrl();
  const response = await axios.post<{
    data?: { sentAt: string; message?: GroupMessageDto };
    sentAt?: string;
  }>(
    `${baseUrl}/send-group-message`,
    {
      sectorId: dto.sectorId,
      groupId: dto.groupId,
      message: dto.message,
      userId: dto.userId ?? null,
      isAnonymous: dto.isAnonymous ?? false,
      replyToMessageId: dto.replyToMessageId ?? null,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SessionService.getToken()}`,
      },
    }
  );
  const data = response.data?.data ?? response.data;
  return {
    sentAt: (data as any)?.sentAt ?? new Date().toISOString(),
    message: (data as any)?.message,
  };
};

const postGroupMedia = async (endpoint: string, dto: SendGroupMediaDto): Promise<{ sentAt: string }> => {
  const baseUrl = getApiUrl();
  const response = await axios.post<{ data?: { sentAt: string }; sentAt?: string }>(
    `${baseUrl}/${endpoint}`,
    {
      sectorId: dto.sectorId,
      groupId: dto.groupId,
      base64File: dto.base64File,
      fileName: sanitizeFileName(dto.fileName),
      mediaType: dto.mediaType || 'application/octet-stream',
      caption: dto.caption || '',
      userId: dto.userId ?? null,
      isAnonymous: dto.isAnonymous ?? false,
      replyToMessageId: dto.replyToMessageId ?? null,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SessionService.getToken()}`,
      },
    }
  );
  const data = response.data?.data ?? response.data;
  return { sentAt: (data as any)?.sentAt ?? new Date().toISOString() };
};

export const sendGroupImage = async (dto: SendGroupMediaDto): Promise<{ sentAt: string }> =>
  postGroupMedia('send-group-image', dto);

export const sendGroupDocument = async (dto: SendGroupMediaDto): Promise<{ sentAt: string }> =>
  postGroupMedia('send-group-document', dto);

export const sendGroupAudioMessage = async (dto: SendGroupMediaDto): Promise<{ sentAt: string }> =>
  postGroupMedia('send-group-audio', dto);

// Função para enviar arquivo
export async function sendFile(fileData: SendFileDto): Promise<MessageResponse> {
  try {
    const isOfficial = SessionService.getSectorIsOfficial();

    if (!isOfficial) {
      let base64Data: string;
      let fileName: string;
      let mediaType: string;

      // Determina se é um arquivo base64 ou File
      if (fileData.base64File) {
        base64Data = fileData.base64File;
        fileName = fileData.fileName;
        mediaType = fileData.mediaType;
      } else if (fileData.file) {
        base64Data = await fileToBase64(fileData.file);
        fileName = fileData.file.name;
        mediaType = fileData.file.type;
      } else {
        throw new Error('Either base64File or file must be provided');
      }

      // Higieniza o nome para evitar quebras com colchetes/espaços/acentos no pipeline do WhatsApp.
      fileName = sanitizeFileName(fileName);

      const isImage = mediaType.startsWith('image/');
      const isAudio = SUPPORTED_AUDIO_TYPES.some(type => mediaType.startsWith(type) || mediaType === type);
      const isDocument = !isImage && !isAudio;

      // Se for áudio, garantir que está em um formato suportado
      if (isAudio && !SUPPORTED_AUDIO_TYPES.includes(mediaType)) {
        throw new Error(`Unsupported audio format. Supported formats are: ${SUPPORTED_AUDIO_TYPES.join(', ')}`);
      }

      let response;
      if (isImage) {
        response = await UnofficialMessageService.sendImage({
          base64File: base64Data,
          fileName,
          mediaType,
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString(),
          replyToMessageId: fileData.replyToMessageId
        });
      } else if (isDocument) {
        response = await UnofficialMessageService.sendDocument({
          base64File: base64Data,
          fileName,
          mediaType,
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString(),
          replyToMessageId: fileData.replyToMessageId
        });
      } else {
        // Para áudio, sempre usar audio/mpeg como tipo
        response = await UnofficialMessageService.sendAudio({
          base64File: base64Data,
          fileName,
          mediaType: 'audio/mpeg',
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString(),
          replyToMessageId: fileData.replyToMessageId
        });
      }

      if (response.status === 'error') {
        throw new Error(response.message || 'Failed to send file');
      }

      return {
        id: Date.now(),
        content: fileData.caption || null,
        mediaType: isImage ? 'image' : isAudio ? 'audio' : 'document',
        mediaUrl: null,
        fileName: fileName || null,
        mimeType: isAudio ? 'audio/mpeg' : mediaType || null,
        sectorId: fileData.sectorId,
        contactID: fileData.contactId,
        sentAt: response.timestamp || fileData.sentAt || new Date().toISOString(),
        isSent: response.status === 'success',
        isRead: false,
        whatsappMessageID: response.data?.whatsappMessageID || undefined,
        replyToMessageID: response.data?.replyToMessageID || fileData.replyToMessageId || undefined,
        repliedMessage: response.data?.repliedMessage || undefined
      };
    }

    // Envio oficial
    if (!fileData.file) {
      throw new Error('File is required for official sending');
    }

    const formData = new FormData();
    // Renomeia o file part do multipart com o nome higienizado para o Content-Disposition.
    formData.append('file', fileData.file, sanitizeFileName(fileData.file.name));
    formData.append('caption', fileData.caption || '');
    formData.append('recipient', fileData.recipient);
    formData.append('recipientPhone', fileData.recipientPhone);
    formData.append('contactId', fileData.contactId.toString());
    formData.append('sectorId', fileData.sectorId.toString());
    if (fileData.isHuman !== undefined) {
      formData.append('isHuman', fileData.isHuman.toString());
    }
    if (fileData.userId !== undefined) {
      formData.append('userId', String(fileData.userId));
    }
    if (fileData.isAnonymous !== undefined) {
      formData.append('isAnonymous', String(fileData.isAnonymous));
    }
    formData.append('sentAt', fileData.sentAt || new Date().toISOString());
    formData.append('timezone', fileData.timezone || APP_TIMEZONE);
    if (fileData.replyToMessageId) {
      formData.append('replyToMessageId', fileData.replyToMessageId);
    }

    console.log('FormData sendo enviado para send-file:', {
      replyToMessageId: fileData.replyToMessageId,
      hasReplyToMessageId: fileData.replyToMessageId ? 'SIM' : 'NÃO'
    });

    const response = await axios.post(
      `${getApiUrl()}/whatsapp/send-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );

    return response.data;

  } catch (error) {
    throw error;
  }
}

// Função para converter WEBM para WAV
const convertWebmToWav = async (webmBlob: Blob): Promise<Blob> => {
  return new Promise(async (resolve, reject) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const arrayBuffer = await webmBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const wavBuffer = audioBufferToWav(audioBuffer);
      const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      resolve(wavBlob);
    } catch (error) {
      reject(error);
    }
  });
};

// Função para converter AudioBuffer para WAV
const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numOfChan = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numOfChan * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = buffer.length * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;
  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  // FMT sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);

  // Data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write the PCM samples
  const channels = [];
  let offset = 44;
  for (let i = 0; i < buffer.numberOfChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      let sample = Math.max(-1, Math.min(1, channels[channel][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }

  return arrayBuffer;
};

const writeString = (view: DataView, offset: number, string: string): void => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

// Função para enviar áudio
export const sendAudioMessage = async (
  audioBlob: Blob,
  recipient: string,
  contactId: number,
  sectorId: number,
  userId?: number,
  isAnonymous?: boolean,
  sentAt?: string,
  replyToMessageId?: string
): Promise<MessageResponse> => {
  try {

    // Converter WEBM para WAV
    const wavBlob = await convertWebmToWav(audioBlob);
    const base64Audio = await blobToBase64(wavBlob);
    const isOfficial = SessionService.getSectorIsOfficial();

    if (!isOfficial) {
      const fileName = `audio_${Date.now()}.wav`;


      const unofficialResponse = await UnofficialMessageService.sendAudio({
        base64File: base64Audio,
        fileName,
        mediaType: 'audio/wav',
        recipient,
        sectorId: sectorId,
        caption: '',
        userId: userId,
        isAnonymous: isAnonymous,
        sentAt: sentAt || new Date().toISOString(),
        replyToMessageId: replyToMessageId
      });

      if (unofficialResponse.status === 'error') {
        throw new Error(unofficialResponse.message || 'Erro ao enviar áudio');
      }

      return {
        id: Date.now(),
        content: null,
        mediaType: 'audio',
        mediaUrl: null,
        fileName,
        mimeType: 'audio/wav',
        sectorId,
        contactID: contactId,
        sentAt: unofficialResponse.timestamp || sentAt || new Date().toISOString(),
        isSent: unofficialResponse.status === 'success',
        isRead: false,
        whatsappMessageID: unofficialResponse.data?.whatsappMessageID || undefined,
        replyToMessageID: unofficialResponse.data?.replyToMessageID || replyToMessageId || undefined,
        repliedMessage: unofficialResponse.data?.repliedMessage || undefined
      };
    }

    // Criar um novo Blob com o áudio convertido
    const audioData = atob(base64Audio);
    const arrayBuffer = new ArrayBuffer(audioData.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < audioData.length; i++) {
      uint8Array[i] = audioData.charCodeAt(i);
    }
    const mp3Blob = new Blob([uint8Array], { type: 'audio/mpeg' });

    // Usar o serviço oficial
    const formData = new FormData();
    formData.append('file', mp3Blob, `audio_${Date.now()}.mp3`);
    formData.append('caption', '');
    formData.append('recipient', recipient);
    formData.append('contactId', contactId.toString());
    formData.append('sectorId', sectorId.toString());
    if (userId !== undefined) {
      formData.append('userId', String(userId));
    }
    if (isAnonymous !== undefined) {
      formData.append('isAnonymous', String(isAnonymous));
    }
    formData.append('sentAt', sentAt || new Date().toISOString());
    formData.append('timezone', APP_TIMEZONE);
    if (replyToMessageId) {
      formData.append('replyToMessageId', replyToMessageId);
    }

    console.log('FormData sendo enviado para send-audio:', {
      replyToMessageId: replyToMessageId,
      hasReplyToMessageId: replyToMessageId ? 'SIM' : 'NÃO'
    });

    const response = await axios.post<MessageResponse>(
      `${getApiUrl()}/whatsapp/send-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado ao enviar áudio:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

// Funções auxiliares
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        try {
          if (typeof reader.result === 'string') {
            const base64Data = reader.result.split(',')[1];
            if (!base64Data) {
              reject(new Error('Failed to extract base64 data'));
              return;
            }
            resolve(base64Data);
          } else {
            reject(new Error('Invalid file data'));
          }
        } catch (error) {
          reject(new Error('Error processing file data'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
    } catch (error) {
      reject(new Error('Error initializing file reader'));
    }
  });
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result as string;
        // Remover qualquer prefixo de data URL existente
        const cleanBase64 = base64String.replace(/^data:audio\/(mp3|mpeg|wav|ogg);base64,/, '');
        resolve(cleanBase64);
      } catch (error) {
        reject(new Error('Erro ao processar áudio para base64'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo de áudio'));
    reader.readAsDataURL(blob);
  });
};

export const determineMediaType = (file: File): string => {
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const audioTypes = SUPPORTED_AUDIO_TYPES;

  if (imageTypes.includes(file.type)) {
    return 'image';
  }
  if (audioTypes.includes(file.type)) {
    return 'audio';
  }
  return 'document';
};

export const normalizeMessage = (message: MessageType): MessageType => {
  if (!message.mediaUrl && !message.attachment?.url) {
    return message;
  }

  if (message.mediaType === 'image' || message.mediaType === 'audio') {
    return {
      ...message,
      attachment: undefined
    };
  }

  if (message.mediaType === 'document' || message.attachment) {
    return {
      ...message,
      mediaUrl: null,
      attachment: {
        type: 'document',
        url: message.mediaUrl || message.attachment?.url || '',
        name: message.fileName || message.attachment?.name || 'arquivo'
      }
    };
  }

  return message;
};

// Função para buscar contatos
export const getWhatsAppContacts = async (sectorId: number): Promise<any[]> => {
  try {
    const response = await axios.get(`/server/api/whatsapp/contact/${sectorId}`);
    return response.data || [];
  } catch (error) {
    return [];
  }
};

// Função para enviar mensagem de sistema (notificação interna no chat)
// Usada para registrar eventos como transferência de contato
export const sendSystemMessage = async (
  contactId: number,
  sectorId: number,
  message: string
): Promise<MessageResponse> => {
  try {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://api.ligchat.com';

    const response = await axios.post(
      `${API_BASE}/messages/system`,
      {
        contactId,
        sectorId,
        content: message,
        mediaType: 'system'
      },
      {
        headers: {
          Authorization: `Bearer ${SessionService.getToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.data || response.data;
  } catch (error) {
    console.error('Erro ao enviar mensagem de sistema:', error);
    // Retornar mensagem local mesmo que falhe para não bloquear a operação
    return {
      id: Date.now(),
      content: message,
      mediaType: 'system',
      mediaUrl: null,
      fileName: null,
      mimeType: null,
      sectorId,
      contactID: contactId,
      sentAt: new Date().toISOString(),
      isSent: true,
      isRead: true
    };
  }
};


